import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, MapPin } from "lucide-react";
import { goTo, useLang } from "@/i18n";
import { AeroImg } from "@/components/AeroImg";

const EASE = [0.16, 1, 0.3, 1];

export function Hero() {
  const { t, lang } = useLang();
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 220]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);
  const lines = [t("hero_l1"), t("hero_l2"), t("hero_l3")];

  return (
    <section id="top" data-testid="hero-section" data-always-dark className="relative min-h-[100svh] overflow-hidden flex items-end grain">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -bottom-56">
        <motion.div
          initial={{ scale: 1.18 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: EASE }}
          className="h-full w-full"
        >
          <AeroImg img="hero" alt="Aircraft ground handling at dusk on Libyan tarmac" eager className="h-full w-full object-cover" />
        </motion.div>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#060B14] via-[#060B14]/72 to-[#060B14]/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#060B14]/80 via-transparent to-[#060B14]/40" />
      <div className="absolute inset-0 grid-lines opacity-60" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 pb-24 pt-40">
        <motion.div
          key={`badge-${lang}`}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
          className="mb-7 inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-950/40 px-4 py-2 backdrop-blur-sm"
          data-testid="hero-badge"
        >
          <MapPin size={13} className="text-blue-400" />
          <span className="font-mono-tag text-[11px] tracking-widest text-blue-300">{t("hero_badge")}</span>
        </motion.div>

        <h1 key={lang} className="max-w-5xl text-4xl sm:text-5xl lg:text-7xl font-extrabold leading-[1.15] tracking-tight text-white" data-testid="hero-headline">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 2 ? "text-blue-500" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.3 + i * 0.16, ease: EASE }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          key={`sub-${lang}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
          className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-300"
          data-testid="hero-subtext"
        >
          {t("hero_sub")}
        </motion.p>

        <motion.div
          key={`cta-${lang}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-request-service-button"
            onClick={() => goTo("contact")}
            className="rounded-full bg-blue-600 px-8 py-4 text-base font-bold text-white shadow-[0_0_36px_rgba(37,99,235,0.5)] hover:bg-blue-500 hover:shadow-[0_0_48px_rgba(37,99,235,0.7)] transition-all duration-300 hover:-translate-y-0.5"
          >
            {t("hero_cta1")}
          </button>
          <button
            data-testid="hero-explore-network-button"
            onClick={() => goTo("airports")}
            className="rounded-full border border-white/25 bg-white/5 px-8 py-4 text-base font-semibold text-white backdrop-blur-sm hover:bg-white/10 hover:border-white/40 transition-all duration-300"
          >
            {t("hero_cta2")}
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1 text-blue-300/70"
        data-testid="hero-scroll-indicator"
      >
        <span className="font-mono-tag text-[10px] tracking-[0.3em] uppercase">{t("hero_scroll")}</span>
        <ChevronDown size={16} className="animate-bounce" />
      </motion.div>
    </section>
  );
}
